import { useMemo, useReducer, Reducer } from 'react';

import { EditEventsAction } from '../../../device/compositions/performance/events/edit-events';
import { DeviceEventFilters, IDeviceEventFilterState } from '../../../device/compositions/performance/events/deviceEventsTypes';
import { AnomalyChartName, ChartName } from '../../../device/compositions/performance/charts/lib/chartNames';
import { DeviceEventName, deviceEventNames } from '../../../../../services/core/eventsTypes';
import { omitShallow } from '../../../../../lib/omitShallow';

function getInitialEventFilters(platformType: string, initiallyEnabled: readonly DeviceEventName[], lockedEvents: readonly DeviceEventName[]): DeviceEventFilters {
  const enabled = new Set<DeviceEventName>([...initiallyEnabled, ...lockedEvents]);
  const filters = deviceEventNames.reduce((acc, name) => {
    acc[name] = enabled.has(name);
    return acc;
  }, {} as DeviceEventFilters);
  // Mirrors getFiltersState's platform stripping: deviceDropped events are not shown for Windows
  // devices, and processCrashed/processHung are not shown for Android devices.
  const eventsToRemove: DeviceEventName[] = platformType === 'android' ? ['processCrashed', 'processHung'] : ['deviceDropped'];
  return omitShallow<DeviceEventFilters>(filters, eventsToRemove);
}

// Forces every locked event back to enabled, overriding whatever onUpdate's own filterState said.
// This is a second, independent guard against a locked event ever being disabled - the primary
// one is EditEventsModal itself refusing to render an interactive checkbox for it (including via
// DISPLAY_ALL) - so state can never diverge from the dialog even if a future caller dispatches
// onUpdate some other way.
// Only re-enables a locked event that filterState actually has a key for - getInitialEventFilters
// (above) strips events the current platformType doesn't support at all (e.g. deviceDropped on
// Windows), and forcing one of those back on here would otherwise resurrect it as a phantom,
// always-true filter (visible via EventsShown) the first time the dialog is used on the wrong
// platform, even though the UI never actually offers that combination today.
function withLockedEventsEnabled(filterState: DeviceEventFilters, lockedEvents: readonly DeviceEventName[]): DeviceEventFilters {
  if (!lockedEvents.length) {
    return filterState;
  }
  const locked = lockedEvents.reduce((acc, name) => {
    if (name in filterState) {
      acc[name] = true;
    }
    return acc;
  }, {} as DeviceEventFilters);
  return { ...filterState, ...locked };
}

// Mirrors editEventsReducer's onUpdate/onApply/onCancel state transitions, but never persists
// anything - it is not necessary to save the event marker selection between sessions.
// onApply simply promotes the current selection to applied, in memory, for this hook's single
// chart, instead of calling saveEventFilters/setChartEventFiltersAction.
// onApplyAll is deliberately not handled: LowPowerSection passes showApplyAll={false} to its
// chart, while DeviceEventsTimelineChart omits EditEvents' handleApplyAll callback. Neither
// displays the button that would dispatch it. It falls to the unreachable default below.
// action.name is part of EditEventsAction's shape (editEventsReducer needs it to index a
// multi-chart DeviceChartEventsFilterState) but is never read here, since this reducer only ever
// manages one chart's state - regardless of whether the caller passes a real, persisted ChartName
// (e.g. LowPowerSection reusing DeviceChargeLevel's own chartNames.deviceChargeLevel) or a
// session-only AnomalyChartName (e.g. RebootsSection/DropsSection's own
// anomalyChartNames.deviceEventsTimeline).
// lockedEvents is closed over (rather than read from state/action) since it is fixed per caller
// (e.g. ['deviceRebooted'] for the reboots timeline, ['deviceDropped'] for the drops timeline)
// and never changes across a single hook's lifetime - see useAnomalyExaminationEventFilters below.
function makeSessionOnlyEventFiltersReducer(lockedEvents: readonly DeviceEventName[]) {
  return function sessionOnlyEventFiltersReducer(state: IDeviceEventFilterState, action: EditEventsAction<ChartName | AnomalyChartName>): IDeviceEventFilterState {
    switch (action.type) {
    case 'onUpdate':
      return { ...state, currentEventFilters: withLockedEventsEnabled(action.filterState, lockedEvents) };
    case 'onApply':
      return { ...state, appliedEventFilters: state.currentEventFilters };
    case 'onCancel':
      return { ...state, currentEventFilters: state.appliedEventFilters };
    /* istanbul ignore next: onApplyAll (see comment above) and any other EditEventsAction<ChartName | AnomalyChartName>
       member are both unreachable here */
    default:
      return state;
    }
  };
}

export interface AnomalyExaminationEventFilters {
  eventFilters: IDeviceEventFilterState,
  dispatchEventFilters: React.Dispatch<EditEventsAction<ChartName | AnomalyChartName>>
}

/**
 * Session-only event marker filters for a single chart on the anomaly examination page: the
 * initial selection is seeded once from initiallyEnabled, edits made via the EditEvents
 * dialog are held only in local state, and nothing is ever written to user settings - so the
 * selection resets on refresh/navigation rather than persisting between sessions.
 *
 * lockedEvents (default none) are forced enabled in both the seeded initial state and on every
 * subsequent onUpdate - they can never be switched off via this hook's state, matching
 * EditEventsModal's own refusal to render an interactive (non-disabled) checkbox for them.
 */
export function useAnomalyExaminationEventFilters(
  platformType: string,
  initiallyEnabled: readonly DeviceEventName[],
  lockedEvents: readonly DeviceEventName[] = []
): AnomalyExaminationEventFilters {
  const initialState = useMemo((): IDeviceEventFilterState => {
    const filters = getInitialEventFilters(platformType, initiallyEnabled, lockedEvents);
    return { appliedEventFilters: filters, currentEventFilters: filters };
  }, [platformType, initiallyEnabled, lockedEvents]);

  const reducer = useMemo(() => makeSessionOnlyEventFiltersReducer(lockedEvents), [lockedEvents]);

  const [eventFilters, dispatchEventFilters] = useReducer<Reducer<IDeviceEventFilterState, EditEventsAction<ChartName | AnomalyChartName>>>(
    reducer,
    initialState
  );

  return { eventFilters, dispatchEventFilters };
}
